import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Modal, Select, Skeleton, Tag } from 'antd';
import type { AllocationStatus, EngagementType, PersonProfile } from '@youkuai/contracts';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import {
  changePersonAllocationStatus,
  createPerson,
  listPeople,
  updatePerson,
} from './people-api.ts';
import { ApiRequestError } from './project-api.ts';

type Filter = 'ALL' | AllocationStatus;
type Dialog = { kind: 'create' } | { kind: 'edit'; person: PersonProfile } |
  { kind: 'allocation'; person: PersonProfile };

const engagementLabels: Record<EngagementType, string> = {
  EMPLOYEE: '全职同事',
  LONG_TERM_EXECUTOR: '长期合作执行人员',
};

const projectRoleLabels: Record<string, string> = {
  PRIMARY_MANAGER: '主要负责人',
  DELIVERY_CONFIRMER: '交付确认',
  MEMBER: '项目成员',
};

function failureText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      PERSON_NAME_LOCKED: '该姓名已经被业务历史引用，不能在普通编辑中直接更名。',
      VERSION_CONFLICT: '人员档案已被其他人更新，请刷新后再试。',
      PERSON_NOT_FOUND: '人员档案不存在或已发生变化。',
      FORBIDDEN: '当前账号没有维护人员档案的权限。',
    };
    return messages[error.code] ?? `操作失败（${error.code}）`;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

function availability(person: PersonProfile) {
  const active = person.active && person.allocationStatus === 'ACTIVE';
  return <span className={`people-availability ${active ? 'is-active' : 'is-paused'}`}><i />
    {active ? '可参与分配' : '暂停新分配'}</span>;
}

export function PeoplePage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const capabilities = new Set(state.kind === 'ready' ? state.session.capabilities : []);
  const canManage = capabilities.has('person.manage');
  const canChangeAllocation = capabilities.has('person.allocation_status');
  const client = useQueryClient();
  const [filter, setFilter] = useState<Filter>('ALL');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [engagementType, setEngagementType] = useState<EngagementType>('EMPLOYEE');
  const [company, setCompany] = useState('');
  const [department, setDepartment] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [skills, setSkills] = useState('');
  const [initialStatus, setInitialStatus] = useState<AllocationStatus>('ACTIVE');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const people = useQuery({ queryKey: ['people', accountId], queryFn: () => listPeople(token),
    enabled: state.kind === 'ready' });
  const mutation = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['people', accountId] });
      setDialog(null);
      setNotice({ type: 'success', text: '人员档案已保存；项目成员、任务、账号与历史财务记录没有被自动改写。' });
    },
    onError: (error) => setNotice({ type: 'error', text: failureText(error) }),
  });

  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (people.data?.items ?? []).filter((person) =>
      (filter === 'ALL' || person.allocationStatus === filter) &&
      (!query || [person.displayName, person.personCode, person.company ?? '', person.department ?? '', person.jobTitle ?? '', ...person.skills]
        .join(' ').toLocaleLowerCase().includes(query)));
  }, [filter, people.data, search]);
  const selected = visible.find((person) => person.personId === selectedId) ?? visible[0] ?? null;
  const total = people.data?.items.length ?? 0;
  const assignable = people.data?.items.filter((person) => person.active && person.allocationStatus === 'ACTIVE').length ?? 0;
  const paused = people.data?.items.filter((person) => person.allocationStatus === 'ALLOCATION_PAUSED').length ?? 0;
  const projectRelations = people.data?.items.reduce((sum, person) => sum + person.activeProjectCount, 0) ?? 0;

  function open(next: Dialog) {
    setNotice(null);
    setReason('');
    const person = next.kind === 'create' ? null : next.person;
    setDisplayName(person?.displayName ?? '');
    setEngagementType(person?.engagementType ?? 'EMPLOYEE');
    setCompany(person?.company ?? '');
    setDepartment(person?.department ?? '');
    setJobTitle(person?.jobTitle ?? '');
    setPhone(person?.phone ?? '');
    setEmail(person?.email ?? '');
    setSkills(person?.skills.join('、') ?? '');
    setInitialStatus(person?.allocationStatus ?? 'ACTIVE');
    setDialog(next);
  }

  async function submit() {
    if (!dialog || reason.trim().length < 2) return;
    if (dialog.kind === 'allocation') {
      await mutation.mutateAsync(() => changePersonAllocationStatus(token, dialog.person.personId, {
        expectedVersion: dialog.person.version,
        allocationStatus: dialog.person.allocationStatus === 'ACTIVE' ? 'ALLOCATION_PAUSED' : 'ACTIVE',
        reason: reason.trim(),
      }));
      return;
    }
    const skillList = skills.split(/[、,，\n]/u).map((skill) => skill.trim()).filter(Boolean);
    const profile = {
      displayName: displayName.trim(),
      engagementType,
      company: company.trim() || null,
      department: department.trim() || null,
      jobTitle: jobTitle.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      skills: [...new Set(skillList)],
      reason: reason.trim(),
    };
    if (dialog.kind === 'create') {
      await mutation.mutateAsync(() => createPerson(token, { ...profile, allocationStatus: initialStatus }));
    } else {
      await mutation.mutateAsync(() => updatePerson(token, dialog.person.personId, {
        ...profile, expectedVersion: dialog.person.version,
      }));
    }
  }

  const formInvalid = !displayName.trim() || !department.trim() || !jobTitle.trim() || reason.trim().length < 2;

  return <main className="page-shell people-page">
    <header className="people-page-head"><div><span className="access-eyebrow">PEOPLE DIRECTORY</span><h1>人员档案</h1>
      <p>统一维护人员身份与可用状态；项目成员、任务分配和账号权限仍分别管理。</p></div>
      <section className="access-summary" aria-label="人员档案摘要">
        <div><span>人员档案</span><strong>{total}</strong><small>全局独立档案</small></div>
        <div><span>可参与分配</span><strong>{assignable}</strong><small>仍需具备项目职责</small></div>
        <div><span>项目关系</span><strong>{projectRelations}</strong><small>当前有效关系</small></div>
        <div><span>暂停分配</span><strong>{paused}</strong><small>历史记录继续保留</small></div>
      </section></header>

    {notice && <Alert className="access-notice" showIcon type={notice.type} title={notice.text} closable onClose={() => setNotice(null)} />}
    {people.error && <Alert className="access-notice" showIcon type="error" title={failureText(people.error)} />}
    <section className="people-toolbar"><div className="access-segmented">{([
      ['ALL', '全部'], ['ACTIVE', '可参与分配'], ['ALLOCATION_PAUSED', '暂停分配'],
    ] as const).map(([value, label]) => <button key={value} className={filter === value ? 'is-active' : ''}
      onClick={() => setFilter(value)}>{label}</button>)}</div>
      <Input.Search allowClear value={search} onChange={(event) => setSearch(event.target.value)}
        placeholder="搜索姓名、公司、部门、岗位或技能" />
      {canManage && <Button type="primary" onClick={() => open({ kind: 'create' })}>新增人员档案</Button>}</section>

    {people.isPending ? <Skeleton active paragraph={{ rows: 12 }} /> : total ? <section className="people-workspace">
      <aside className="people-index"><header><span><b>{visible.length}</b> 份人员档案</span><small>全局人员主数据</small></header>
        <div>{visible.map((person) => <button key={person.personId}
          className={selected?.personId === person.personId ? 'is-active' : ''}
          onClick={() => setSelectedId(person.personId)}><i>{person.displayName.slice(0, 1)}</i><span>
            <small>{person.department ?? '部门待完善'} · {person.jobTitle ?? '岗位待完善'}</small><strong>{person.displayName}</strong>
            <em>{person.skills.slice(0, 2).join(' · ') || '能力标签待完善'}</em></span><span>{availability(person)}
            <small>{person.activeProjectCount} 个项目 · {person.activeTaskCount} 个进行中任务</small></span></button>)}
          {!visible.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有匹配的人员" />}</div></aside>
      <article className="people-inspector">{selected ? <><header className="people-inspector-head"><div><i>{selected.displayName.slice(0, 1)}</i><span>
        <small>{selected.department ?? '部门待完善'} · {selected.jobTitle ?? '岗位待完善'}</small><h2>{selected.displayName}</h2>
        <p>{selected.personCode} · 建档于 {new Date(selected.createdAt).toLocaleDateString('zh-CN')}</p></span></div><div>{availability(selected)}
        {canManage && <Button onClick={() => open({ kind: 'edit', person: selected })}>编辑资料</Button>}
        {canChangeAllocation && <Button danger={selected.allocationStatus === 'ACTIVE'} onClick={() => open({ kind: 'allocation', person: selected })}>
          {selected.allocationStatus === 'ACTIVE' ? '暂停新分配' : '恢复参与分配'}</Button>}</div></header>
        <div className="people-inspector-body"><section className={`people-availability-card ${selected.allocationStatus === 'ACTIVE' ? 'is-active' : 'is-paused'}`}>
          <i>{selected.allocationStatus === 'ACTIVE' ? '⌁' : '◷'}</i><span><small>PEOPLE AVAILABILITY</small>
            <h3>{selected.allocationStatus === 'ACTIVE' ? '可进入符合职责的新任务候选名单' : '已停止进入任何新的分配候选名单'}</h3>
            <p>{selected.allocationStatus === 'ACTIVE' ? '仍需先加入具体项目并具备可分配职责；可用状态本身不会授予项目权限。' : '已有项目关系、任务、材料、费用与结算历史全部保留。'}</p></span>
          <strong>{selected.activeTaskCount}<small>进行中任务</small></strong></section>
          <section className="people-facts"><div><span>联系电话</span><strong>{selected.phone ?? '尚未填写'}</strong><small>人员档案联系方式</small></div>
            <div><span>工作邮箱</span><strong>{selected.email ?? '尚未填写'}</strong><small>人员档案联系方式</small></div>
            <div><span>所属公司</span><strong>{selected.company ?? '尚未填写'}</strong><small>用于结算汇总与付款核对</small></div>
            <div><span>当前项目</span><strong>{selected.activeProjectCount}</strong><small>{selected.projects.length} 条有效关系</small></div>
            <div><span>登录账号</span><strong>{selected.account ? `@${selected.account.username}` : '尚未创建'}</strong><small>{selected.account ? '账号权限在系统管理维护' : '人员档案不等于登录账号'}</small></div></section>
          <section className="people-skills"><header><div><span className="access-eyebrow">CAPABILITY PROFILE</span><h3>能力标签</h3>
            <p>用于理解人员擅长方向，不会自动授予项目职责。</p></div>{canManage && <Button onClick={() => open({ kind: 'edit', person: selected })}>编辑</Button>}</header>
            <div>{selected.skills.map((skill) => <Tag key={skill}>{skill}</Tag>)}{!selected.skills.length && <span>暂未设置能力标签</span>}</div></section>
          <section className="people-projects"><header><div><span className="access-eyebrow">PROJECT RELATIONS</span><h3>参与项目</h3></div><small>{selected.projects.length} 条当前有效关系</small></header>
            <div>{selected.projects.map((project) => <Link key={project.projectId} to={`/projects/${project.projectId}/team`}><i>{project.projectName.slice(0,1)}</i><span>
              <small>{project.projectCode}</small><b>{project.projectName}</b><em>{project.roles.map((role) => projectRoleLabels[role] ?? role).join(' · ')}</em></span>
              <Tag>{project.projectStatus}</Tag><b>›</b></Link>)}{!selected.projects.length && <span className="people-empty-line">尚未加入任何项目；请从具体项目的成员页建立关系。</span>}</div></section>
          <section className="people-workload"><header><div><span className="access-eyebrow">WORKLOAD</span><h3>跨项目任务概况</h3></div></header>
            <div><article><span>进行中任务</span><strong>{selected.activeTaskCount}</strong><small>暂停分配不会自动改派</small></article>
              <article><span>已完成任务</span><strong>{selected.completedTaskCount}</strong><small>历史执行记录持续保留</small></article>
              <article><span>人员类型</span><strong>{engagementLabels[selected.engagementType]}</strong><small>用于结算周期与用工识别</small></article></div></section>
          <section className="access-rule"><i><Icon name="info" size={14} /></i><span><b>人员档案、项目职责、登录账号相互独立</b><small>新增人员不会自动加入项目或创建账号；暂停分配也不会删除成员关系、任务、材料和结算历史。</small></span></section>
        </div></> : <Empty description="选择一份人员档案" />}</article>
    </section> : <section className="people-directory-empty"><i>人</i><span className="access-eyebrow">PEOPLE DIRECTORY</span><h2>还没有人员档案</h2>
      <p>人员档案是项目成员和任务分配的上游主数据，但不会自动创建账号或加入项目。</p>
      {canManage && <Button type="primary" onClick={() => open({ kind: 'create' })}>建立第一份人员档案</Button>}</section>}

    <Modal open={!!dialog} onCancel={() => setDialog(null)} onOk={() => void submit()} confirmLoading={mutation.isPending}
      okText={dialog?.kind === 'allocation' ? (dialog.person.allocationStatus === 'ACTIVE' ? '确认暂停' : '确认恢复') : '保存档案'}
      cancelText="取消" width={dialog?.kind === 'allocation' ? 560 : 720}
      okButtonProps={{ disabled: dialog?.kind === 'allocation' ? reason.trim().length < 2 : formInvalid }}
      title={dialog?.kind === 'create' ? '新增人员档案' : dialog?.kind === 'edit' ? `编辑 ${dialog.person.displayName}`
        : dialog?.kind === 'allocation' ? `${dialog.person.allocationStatus === 'ACTIVE' ? '暂停' : '恢复'} ${dialog.person.displayName} 的新分配` : ''}>
      {(dialog?.kind === 'create' || dialog?.kind === 'edit') && <div className="people-form"><div className="people-form-grid">
        <label><span>姓名 <b>*</b></span><Input value={displayName} onChange={(event) => setDisplayName(event.target.value)}
          {...(dialog.kind === 'edit' && dialog.person.nameLocked ? { disabled: true } : {})} />
          {dialog.kind === 'edit' && dialog.person.nameLocked && <small>姓名已被账号、项目或任务历史引用，不在普通编辑中直接更名。</small>}</label>
        <label><span>人员类型 <b>*</b></span><Select value={engagementType} onChange={setEngagementType} options={[
          { value: 'EMPLOYEE', label: engagementLabels.EMPLOYEE },
          { value: 'LONG_TERM_EXECUTOR', label: engagementLabels.LONG_TERM_EXECUTOR },
        ]} /></label>
        <label><span>所属公司</span><Input value={company} maxLength={160} onChange={(event) => setCompany(event.target.value)} /></label>
        <label><span>所属部门 <b>*</b></span><Input value={department} onChange={(event) => setDepartment(event.target.value)} /></label>
        <label><span>岗位 <b>*</b></span><Input value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} /></label>
        <label><span>联系电话</span><Input value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
        <label><span>工作邮箱</span><Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label className="is-wide"><span>能力标签</span><Input value={skills} onChange={(event) => setSkills(event.target.value)} placeholder="使用顿号或逗号分隔，例如：选题策划、脚本撰写" /></label>
        {dialog.kind === 'create' && <label><span>初始可分配状态</span><Select value={initialStatus} onChange={setInitialStatus} options={[
          { value: 'ACTIVE', label: '可参与分配' }, { value: 'ALLOCATION_PAUSED', label: '暂停新分配' },
        ]} /></label>}
      </div><div className="people-form-rule"><i><Icon name="info" size={14} /></i><span><b>这里保存的是人员主档</b><small>项目成员关系、项目职责和登录账号都需在各自入口单独维护。</small></span></div></div>}
      {dialog?.kind === 'allocation' && <div className="people-status-dialog"><section><i>{dialog.person.displayName.slice(0,1)}</i><span><small>{dialog.person.department} · {dialog.person.jobTitle}</small>
        <b>{dialog.person.displayName}</b><em>{dialog.person.allocationStatus === 'ACTIVE' ? '变更后不再出现于新任务候选名单' : '变更后可按项目职责重新进入候选名单'}</em></span></section>
        <div><article><span>参与项目</span><b>{dialog.person.activeProjectCount}</b><small>条关系继续保留</small></article>
          <article><span>未结束任务</span><b>{dialog.person.activeTaskCount}</b><small>不自动改派或取消</small></article>
          <article><span>历史完成</span><b>{dialog.person.completedTaskCount}</b><small>执行记录继续保留</small></article></div></div>}
      {dialog && <label className="access-reason"><span>变更原因 <b>*</b></span><Input.TextArea rows={3} maxLength={500} value={reason}
        onChange={(event) => setReason(event.target.value)} placeholder="说明本次人员资料或可分配状态变更的原因" /></label>}
    </Modal>
  </main>;
}
